import React, { useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import Breakpoint from 'components/Common/Breakpoint'
import Image from 'components/Common/Image'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import { getShareModalConfig } from 'lib/getShareModalConfig'
import OfferBNPLAd from '../OfferBNPLAd'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import TextButton from 'components/Luxkit/Button/TextButton'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import ShareButton from 'components/Common/ShareWithFriendsButton'
import InlineGroup from 'components/Common/InlineGroup'
import FlashOfferSummary from '../FlashHotel/FlashOfferSummary'
import OfferDescription from '../OfferDescription'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import LineEyeIcon from 'components/Luxkit/Icons/line/LineEyeIcon'
import Caption from 'components/Luxkit/Typography/Caption'
import { getOfferExtra, OFFER_VIEWS_24_HOURS_THRESHOLD } from 'selectors/offerExtraSelector'

import { useAppDispatch } from 'hooks/reduxHooks'
import { fetchOfferExtraById } from 'actions/OfferActions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import DepositsAvailableTag from 'components/Common/SecureWithDeposit/DepositsAvailableTag'
import { getDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/depositsUtils'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import GeoContext from 'contexts/geoContext'
import {
  getCheapestAvailablePackageOption,
  getCheapestSelectedPackageOption,
  OfferPackageOptionView,
  getTacticalPackageOptions,
} from 'selectors/offerPageSelectors'
import OfferFlexiblePaymentsInfo from '../OfferFlexiblePaymentsInfo'
import { getWeeklyInstalmentAmount, isInstalmentsAvailableOnOffer } from 'lib/payInInstalmentsUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import UltraLuxUSPs from './UltraLuxUSPs'
import AlwaysOnSummary from '../AlwaysOn/AlwaysOnSummary'
import config from 'constants/config'
import SolidPlayIcon from 'components/Luxkit/Icons/solid/SolidPlayIcon'
import { getInstalmentsAbTestEnabled, isIntroMapEnabled } from 'selectors/featuresSelectors'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import PropertyMap from 'components/Common/PropertyMap/PropertyMap'
import { OFFER_SUMMARY_MAP_HEIGHT, PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import OfferPagePropertyTitle from '../OfferPagePropertyTitle'
import { useBusinessTravellerStickyBudgetBarHeightContext } from 'businessTraveller/utils/useBusinessBudgetBarHeightContext'
import OfferPromotions from 'components/OfferPage/OfferPromotions'

const OfferIntroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.sm} {
    grid-template-columns: 1fr min-content;
    grid-gap: ${rem(16)} ${rem(48)};
  }
`

const ContentColumn = styled.div`
  > * + * {
    margin-top: ${rem(16)};
  }
`

const OfferBadgeTagSection = styled.div`
  > * + * {
    margin-left: ${rem(8)};
  }
`

const SummaryColumn = styled.div`
  display: none;

  ${mediaQueryUp.sm} {
    display: flex;
    flex-direction: column;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const SummaryBlock = styled.div`
  ${mediaQueryUp.sm} {
    position: sticky;
    top: ${rem(12)};
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const HotelSummary = styled(FlashOfferSummary)`
  min-width: ${rem(308)};

  ${mediaQueryUp.md} {
    min-width: ${rem(382)};
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PropertyLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.md} {
    margin-left: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

const OfferIntroWrapper = styled(LayoutContainer)`
  padding: ${rem(20)};

  ${mediaQueryUp.sm} {
    padding: ${rem(20)} ${rem(40)};
  }

  ${mediaQueryUp.md} {
    padding: ${rem(32)} ${rem(12)};
  }

  > * + * {
    margin-top: ${rem(48)};
  }
`

const OfferViews = styled.div`
  display: flex;
  padding: ${rem(8)};
  justify-content: center;
  color: ${props => props.theme.palette.messaging.fast.main};
  background-color: ${props => props.theme.palette.messaging.fast.light};
`

const MobileOfferViews = styled(OfferViews)`
  position: relative;
  top: -${rem(20)};
  margin: 0 -${rem(20)};
`

interface Props {
  cheapestSelectedOption?: OfferPackageOptionView;
  cheapestOption: OfferPackageOptionView;
  promotions: Array<OfferPackageOptionView>;
  checkIn: moment.Moment;
  offer: App.Offer;
  customerPortalURL: string;
  regionCode: string;
  pathname: string;
  memberId: string;
  onWatchVideo: () => void;
  className?: string;
  onViewPackageOptions: () => void;
  flightDestination?: App.Airport;
  offerExtra: App.OfferExtra;
  instalmentsABTestEnabled: boolean;
  defaultDepositAmountPercentage: number;
  isMapEnabled: boolean;
}

function UltraLuxIntro(props: Props) {
  const {
    cheapestOption,
    promotions,
    cheapestSelectedOption,
    checkIn,
    offer,
    onWatchVideo,
    memberId,
    pathname,
    regionCode,
    customerPortalURL,
    className,
    onViewPackageOptions,
    flightDestination,
    offerExtra,
    instalmentsABTestEnabled,
    defaultDepositAmountPercentage,
    isMapEnabled,
  } = props

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const property = offer.property
  const location = [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')

  const shareConfig = getShareModalConfig({
    customerPortalURL,
    image: offer.images[0],
    memberId,
    offerId: offer.id,
    offerLocation: location,
    pathname,
    region: regionCode,
    type: 'offer',
    utmCampaign: 'webshare',
  })
  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])
  const { currentCurrency, showTaxExclusivePrices } = useContext(GeoContext)

  const showBadge = !!offer.badge && config.BRAND === 'luxuryescapes'

  const packageView = cheapestSelectedOption ?? cheapestOption
  const { hotelPrice, propertyFees } = packageView
  const totalPrice = showTaxExclusivePrices ? hotelPrice : hotelPrice + propertyFees

  const depositsAvailable = isDepositAvailableOnOffer(offer, currentCurrency, checkIn, totalPrice)
  const instalmentsAvailable = instalmentsABTestEnabled && isInstalmentsAvailableOnOffer(offer, currentCurrency, checkIn)
  const instalmentWeeklyAmount = getWeeklyInstalmentAmount(hotelPrice, checkIn, offer, defaultDepositAmountPercentage)

  const urgencyToday = useMemo(() => {
    if (offerExtra) {
      return {
        isActive: offerExtra.viewsInLast24Hours >= OFFER_VIEWS_24_HOURS_THRESHOLD,
        captionText: `${offerExtra.viewsInLast24Hours} people viewed this offer today`,
        Icon: LineEyeIcon,
      }
    }
    return { isActive: false }
  }, [offerExtra])

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchOfferExtraById(offer.id))
  }, [dispatch, offer.id])

  const businessTravellerStickyBudgetBarStyle = useBusinessTravellerStickyBudgetBarHeightContext()

  return (<OfferIntroWrapper>
    { urgencyToday.isActive && <Breakpoint max="xs">
      <MobileOfferViews>
        <InlineGroup vertical="center">
          <urgencyToday.Icon />&nbsp;
          <Caption variant="medium" weight="bold">{urgencyToday.captionText}</Caption>
        </InlineGroup>
      </MobileOfferViews>
    </Breakpoint> }
    <OfferIntroGrid className={className} data-testid="offer-intro">
      <ContentColumn>
        <TripGuard>
          <CSSBreakpoint max="xs" >
            <InlineGroup vertical="center" spacing="sm" horizontal="end">
              <ShareModal shareConfig={shareConfig}>
                <ShareButton />
              </ShareModal>
              <BookmarkButton offer={offer} />
            </InlineGroup>
          </CSSBreakpoint>
        </TripGuard>
        <InlineGroup wraps horizontal="between" spacing="sm" vertical="center">
          <LabelGroup>
            {(depositsAvailable || instalmentsAvailable) && <DepositsAvailableTag offerType={offer.type} depositAmountPercentage={depositAmountPercentage} depositThresholds={offer.depositThresholds}/>}
            <OfferUrgencyLabels offer={offer} showLimitedAvailability />
          </LabelGroup>
          <TripGuard inverse>
            <CSSBreakpoint max="xs" >
              <ShareModal shareConfig={shareConfig}>
                <ShareButton outdent="right" />
              </ShareModal>
            </CSSBreakpoint>
          </TripGuard>
        </InlineGroup>
        <Header>
          <VerticalSpacer gap={8}>
            <BodyTextBlock variant="medium" data-testid="offer-location" startIcon={<LineMapMarkerIcon />}>
              {location}
            </BodyTextBlock>
            <OfferPagePropertyTitle propertyName={property.name} />
            <Subtitle variant="subtitle2" as="h2">{offer.name}</Subtitle>
          </VerticalSpacer>
          {property.logoImageId &&
            <PropertyLogo
            dpr={2}
            height={75}
            enhancedFormats={false}
            id={property.logoImageId}
            alt={`${property.name || 'Vendor'} logo`}
          />}
        </Header>
        {(!!offer.badge || offer.bundledWithFlightsOnly) && <OfferBadgeTagSection>
          {showBadge && <OfferBadgeTag badge={offer.badge} />}
        </OfferBadgeTagSection>}

        {offer.vimeoVideoId &&
          <TextButton kind="secondary" startIcon={<SolidPlayIcon />} onClick={onWatchVideo}>
            Play video
          </TextButton>
          }
        {offer.description && <OfferDescription description={offer.description} />}
        {offer.additionalDescription &&
          <MarkdownRender type="compact" content={offer.additionalDescription} />
        }
        <CSSBreakpoint max="xs">
          {isMapEnabled && property && <PropertyMap
            height={OFFER_SUMMARY_MAP_HEIGHT}
            wrapperStyle={{ height: OFFER_SUMMARY_MAP_HEIGHT }}
            markers={[{ id: property.id, type: PROPERTY_MAP_MARKER_TYPE, latitude: property.latitude, longitude: property.longitude }]}
          />}
        </CSSBreakpoint>
        {promotions.length > 0 && <OfferPromotions offer={offer} promotions={promotions} />}
      </ContentColumn>
      <SummaryColumn>
        <CSSBreakpoint min="sm">
          <InlineGroup vertical="center" spacing="sm" horizontal="end">
            <TripGuard inverse>
              <ShareModal shareConfig={shareConfig}>
                <ShareButton outdent="right" />
              </ShareModal>
            </TripGuard>
            <TripGuard>
              <ShareModal shareConfig={shareConfig}>
                <ShareButton />
              </ShareModal>
            </TripGuard>
            <TripGuard>
              <BookmarkButton offer={offer} />
            </TripGuard>
          </InlineGroup>
        </CSSBreakpoint>
        <SummaryBlock style={businessTravellerStickyBudgetBarStyle}>
          <div>
            { urgencyToday.isActive && <OfferViews>
              <InlineGroup vertical="center">
                <urgencyToday.Icon />&nbsp;
                <Caption variant="medium" weight="bold">{urgencyToday.captionText}</Caption>
              </InlineGroup>
            </OfferViews> }
            <HotelSummary
              as={offer.type === 'tactical_ao_hotel' ? AlwaysOnSummary : undefined}
              onViewPackageOptions={onViewPackageOptions}
              flightDestination={flightDestination}
            />
            {isMapEnabled && property && <PropertyMap
              height={OFFER_SUMMARY_MAP_HEIGHT}
              wrapperStyle={{ height: OFFER_SUMMARY_MAP_HEIGHT }}
              markers={[{ id: property.id, type: PROPERTY_MAP_MARKER_TYPE, latitude: property.latitude, longitude: property.longitude }]}
            />}
          </div>
          {(depositsAvailable && instalmentsAvailable) &&
            <OfferFlexiblePaymentsInfo offer={offer} currency={currentCurrency} instalmentWeeklyAmount={instalmentWeeklyAmount} defaultDepositAmountPercentage={defaultDepositAmountPercentage} />
          }
          <OfferBNPLAd prices={allPrices} />
        </SummaryBlock>
      </SummaryColumn>
    </OfferIntroGrid>
    <UltraLuxUSPs />
  </OfferIntroWrapper>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  promotions: getTacticalPackageOptions(state, appState),
  cheapestSelectedOption: getCheapestSelectedPackageOption(appState, state),
  cheapestOption: getCheapestAvailablePackageOption(state, appState),
  checkIn: state.checkInDate,
  offer: state.offer,
  regionCode: appState.geo.currentRegionCode,
  pathname: appState.router.location.pathname,
  customerPortalURL: appState.config.customerPortalURL,
  memberId: appState.auth.account.memberId,
  offerExtra: getOfferExtra(appState, state.offer?.id),
  instalmentsABTestEnabled: getInstalmentsAbTestEnabled(appState),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  isMapEnabled: isIntroMapEnabled(appState),
}))(UltraLuxIntro)
